import React from 'react';
import ComingSoonCalculator from '../shared/ComingSoonCalculator';

/**
 * ComingSoonCalculatorWrapper component
 * Wrapper for calculators that are still in development
 * 
 * @param {Object} props
 * @param {string} props.calculatorType - Type of calculator (e.g., 'Discount', 'Statistics')
 */
const ComingSoonCalculatorWrapper = ({ calculatorType }) => {
  // Define calculator metadata based on type
  const getCalculatorMetadata = () => {
    switch (calculatorType) {
      case 'Discount':
        return {
          calculatorName: 'Discount Calculator',
          description: 'Calculate discounts, sale prices, and savings with our comprehensive Discount Calculator. Perfect for shoppers and retailers to quickly determine prices after percentage or fixed amount discounts.',
          category: 'financial',
          expectedRelease: 'Q2 2025',
          features: [
            'Calculate final price after percentage discounts',
            'Determine savings amount from original price',
            'Compare multiple discount options',
            'Calculate bulk purchase discounts',
            'Visualize savings with interactive charts'
          ],
          relatedCalculators: [
            { name: 'Percentage Calculator', path: '/math/percentage-calculator' },
            { name: 'Tax Calculator', path: '/financial/tax-calculator' },
            { name: 'Investment Calculator', path: '/financial/investment-calculator' }
          ]
        };
      
      case 'Tip':
        return {
          calculatorName: 'Tip Calculator',
          description: 'Calculate appropriate tips for restaurants, services, and more with our easy-to-use Tip Calculator. Split bills among multiple people and ensure fair tipping.',
          category: 'financial',
          expectedRelease: 'Q2 2025',
          features: [
            'Calculate tips based on percentage',
            'Split bills among multiple people',
            'Adjust tip percentage based on service quality',
            'Include or exclude tax from tip calculation',
            'Save favorite tip percentages for quick access'
          ],
          relatedCalculators: [
            { name: 'Percentage Calculator', path: '/math/percentage-calculator' },
            { name: 'Tax Calculator', path: '/financial/tax-calculator' }
          ]
        };
      
      case 'Statistics':
        return {
          calculatorName: 'Statistics Calculator',
          description: 'Perform statistical analysis with our comprehensive Statistics Calculator. Calculate mean, median, mode, standard deviation, and more for your data sets.',
          category: 'math',
          expectedRelease: 'Q3 2025',
          features: [
            'Calculate mean, median, and mode',
            'Determine standard deviation and variance',
            'Generate frequency distributions',
            'Perform correlation analysis',
            'Create box plots and histograms',
            'Export results to CSV or PDF'
          ],
          relatedCalculators: [
            { name: 'Scientific Calculator', path: '/math/scientific-calculator' },
            { name: 'Percentage Calculator', path: '/math/percentage-calculator' }
          ]
        };
      
      case 'Geometry':
        return {
          calculatorName: 'Geometry Calculator',
          description: 'Calculate areas, volumes, and properties of various geometric shapes with our Geometry Calculator. Perfect for students, teachers, and professionals working with geometric calculations.',
          category: 'math',
          expectedRelease: 'Q3 2025',
          features: [
            'Calculate areas and perimeters of 2D shapes',
            'Determine volumes and surface areas of 3D objects',
            'Solve triangle problems using trigonometry',
            'Calculate angles and distances',
            'Interactive shape visualizations',
            'Step-by-step solution explanations'
          ],
          relatedCalculators: [
            { name: 'Scientific Calculator', path: '/math/scientific-calculator' },
            { name: 'Unit Conversion Calculator', path: '/math/unit-conversion-calculator' }
          ]
        };
      
      case 'Fraction':
        return {
          calculatorName: 'Fraction Calculator',
          description: 'Perform calculations with fractions using our Fraction Calculator. Add, subtract, multiply, and divide fractions with ease and convert between fractions, decimals, and percentages.',
          category: 'math',
          expectedRelease: 'Q4 2025',
          features: [
            'Add, subtract, multiply, and divide fractions',
            'Convert between fractions, decimals, and percentages',
            'Simplify fractions to lowest terms',
            'Work with mixed numbers and improper fractions',
            'Step-by-step solution explanations'
          ],
          relatedCalculators: [
            { name: 'Scientific Calculator', path: '/math/scientific-calculator' },
            { name: 'Percentage Calculator', path: '/math/percentage-calculator' }
          ]
        };
      
      case 'Macro':
        return {
          calculatorName: 'Macronutrient Calculator',
          description: 'Determine your optimal macronutrient intake with our Macronutrient Calculator. Calculate protein, carbohydrate, and fat requirements based on your goals, body composition, and activity level.',
          category: 'fitness',
          expectedRelease: 'Q2 2025',
          features: [
            'Calculate macronutrient ratios for different goals',
            'Adjust based on activity level and body composition',
            'Track daily macronutrient intake',
            'Generate meal plans based on macronutrient targets',
            'Visualize macronutrient distribution with charts'
          ],
          relatedCalculators: [
            { name: 'BMI Calculator', path: '/fitness/bmi-calculator' },
            { name: 'Calorie Calculator', path: '/fitness/calorie-calculator' },
            { name: 'Ideal Weight Calculator', path: '/fitness/ideal-weight-calculator' }
          ]
        };
      
      default:
        return {
          calculatorName: `${calculatorType} Calculator`,
          description: `Our ${calculatorType} Calculator is currently under development. We're working hard to bring you this new feature soon.`,
          category: 'other',
          expectedRelease: 'Coming Soon',
          features: [
            'Comprehensive calculation features',
            'User-friendly interface',
            'Detailed results and explanations',
            'Save and export functionality'
          ],
          relatedCalculators: []
        };
    }
  };
  
  const metadata = getCalculatorMetadata();
  
  return (
    <ComingSoonCalculator 
      calculatorName={metadata.calculatorName}
      description={metadata.description}
      category={metadata.category}
      expectedRelease={metadata.expectedRelease}
      features={metadata.features}
      relatedCalculators={metadata.relatedCalculators}
    />
  );
};

export default ComingSoonCalculatorWrapper;
